import { Routes, Route, Navigate, useLocation, useNavigationType } from 'react-router-dom'
import { lazy, Suspense, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from './hooks/useAuth'
import PageTransition from './components/ui/PageTransition'
import CookieBanner from './components/CookieBanner'
import { pageBackground, textMuted } from './styles/tokens'
// LoginPage stays sync — it's the unauthenticated landing surface and is
// almost always the first paint for cold users. Every other page is
// route-lazy so the initial bundle only ships LoginPage + the layout
// shell (App, AuthProvider, ThemeProvider, etc.).
import LoginPage from './pages/LoginPage'

const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'))
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'))
const EmailChangeConfirmPage = lazy(() => import('./pages/EmailChangeConfirmPage'))
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'))
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'))
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'))
const TermsPage = lazy(() => import('./pages/TermsPage'))
const Dashboard = lazy(() => import('./pages/Dashboard'))
const Onboarding = lazy(() => import('./pages/Onboarding'))
const CoursePlayer = lazy(() => import('./pages/CoursePlayer'))
const CourseOutlinePage = lazy(() => import('./pages/CourseOutlinePage'))
// Dev-only showcase pages. The lazy imports stay inside the DEV branch so
// Vite's build-time `import.meta.env.DEV` constant prunes them entirely
// (and their chunks) from production output. See `devOnlyRoutes()` below.
const Showcase = import.meta.env.DEV ? lazy(() => import('./pages/Showcase')) : null
const ExerciseShowcase = import.meta.env.DEV ? lazy(() => import('./pages/ExerciseShowcase')) : null
const CoverShowcase = import.meta.env.DEV ? lazy(() => import('./pages/CoverShowcase')) : null
const MyCourses = lazy(() => import('./pages/MyCourses'))
const Stats = lazy(() => import('./pages/Stats'))
const Settings = lazy(() => import('./pages/Settings'))

// Top-level loading state — used both during auth bootstrap (before we
// know whether the user is signed in) and as the Suspense fallback while
// a lazy route chunk is fetching. Wraps the V2 page background so the
// gradient is visible immediately on hard refresh, and uses the V2 brand
// spinner colours so the loading state feels continuous with the
// destination page rather than a different app.
// Routes that exist only for development reference. The `import.meta.env.DEV`
// guard is a Vite build-time constant — in production it folds to `false`,
// React renders nothing, and the lazy() chunks (created at top-of-module
// also under DEV) are pruned. Production users hitting `/showcase/*` or
// `/covers` fall through to the `*` route (NotFoundPage).
function devOnlyRoutes() {
  if (!import.meta.env.DEV) return null
  if (!Showcase || !ExerciseShowcase || !CoverShowcase) return null
  return (
    <>
      <Route path="/showcase" element={<PageTransition><Showcase /></PageTransition>} />
      <Route path="/showcase/exercises" element={<PageTransition><ExerciseShowcase /></PageTransition>} />
      <Route path="/covers" element={<PageTransition><CoverShowcase /></PageTransition>} />
    </>
  )
}

// ScrollToTop resets window scroll on PUSH/REPLACE navigations so a
// click on a Link doesn't inherit the previous page's scroll position
// (e.g. clicking the privacy-policy link mid-Settings was landing the
// learner partway down the policy because the window scrollTop carried
// over). POP navigations (browser back/forward) are skipped so the
// browser's native scroll restoration still works. No UI.
function ScrollToTop() {
  const { pathname } = useLocation()
  const navType = useNavigationType()
  useEffect(() => {
    if (navType !== 'POP') {
      window.scrollTo(0, 0)
    }
  }, [pathname, navType])
  return null
}

function RouteSpinner() {
  const { t } = useTranslation('common')
  return (
    <div
      role="status"
      aria-busy="true"
      aria-label={t('skeletons.loading')}
      className={`${pageBackground} flex flex-col items-center justify-center gap-3`}
    >
      <div className="h-5 w-5 animate-spin rounded-full border-2 border-black/[0.06] border-t-v2-purple dark:border-white/10 dark:border-t-v2-purple-dark" />
      <p className={`text-sm ${textMuted}`}>{t('skeletons.loading')}...</p>
    </div>
  )
}

export default function App() {
  const { user, loading } = useAuth()
  const location = useLocation()

  if (loading) {
    return <RouteSpinner />
  }

  return (
    <Suspense fallback={<RouteSpinner />}>
      {/* Skip link — invisible until keyboard-focused, lets screen-reader
          and keyboard users bypass the sidebar / topnav nav forest on
          every page (WCAG 2.4.1). Targets `#main` which Shell renders
          on the <main> wrapper around the page content. */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-[100] focus:rounded-lg focus:bg-v2-purple focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-white focus:shadow-v2-btn focus:outline-none focus:ring-2 focus:ring-v2-purple/40"
      >
        Skip to main content
      </a>
      <CookieBanner />
      <ScrollToTop />
      <Routes location={location} key={location.pathname}>
        <Route
          path="/login"
          element={user ? <Navigate to="/" replace /> : <PageTransition><LoginPage /></PageTransition>}
        />
        <Route
          path="/forgot-password"
          element={<PageTransition><ForgotPasswordPage /></PageTransition>}
        />
        <Route
          path="/reset-password"
          element={<PageTransition><ResetPasswordPage /></PageTransition>}
        />
        <Route
          path="/email-change-confirm"
          element={<PageTransition><EmailChangeConfirmPage /></PageTransition>}
        />
        <Route
          path="/verify-email"
          element={<PageTransition><VerifyEmailPage /></PageTransition>}
        />
        <Route
          path="/privacy"
          element={<PageTransition><PrivacyPage /></PageTransition>}
        />
        <Route
          path="/terms"
          element={<PageTransition><TermsPage /></PageTransition>}
        />
        <Route
          path="/"
          element={user ? <PageTransition><Dashboard /></PageTransition> : <Navigate to="/login" replace />}
        />
        <Route
          path="/new"
          element={user ? <PageTransition><Onboarding /></PageTransition> : <Navigate to="/login" replace />}
        />
        <Route
          path="/courses"
          element={user ? <PageTransition><MyCourses /></PageTransition> : <Navigate to="/login" replace />}
        />
        <Route
          path="/course/:courseId"
          element={user ? <PageTransition><CoursePlayer /></PageTransition> : <Navigate to="/login" replace />}
        />
        <Route
          path="/course/:courseId/outline"
          element={user ? <PageTransition><CourseOutlinePage /></PageTransition> : <Navigate to="/login" replace />}
        />
        <Route
          path="/stats"
          element={user ? <PageTransition><Stats /></PageTransition> : <Navigate to="/login" replace />}
        />
        <Route
          path="/settings"
          element={user ? <PageTransition><Settings /></PageTransition> : <Navigate to="/login" replace />}
        />
        {/* Dev-only showcase routes; tree-shaken out of production builds. */}
        {devOnlyRoutes()}
        <Route path="*" element={<PageTransition><NotFoundPage /></PageTransition>} />
      </Routes>
    </Suspense>
  )
}
